import React from 'react';
import { Box, Breadcrumbs, Link, Typography, useTheme, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';

/**
 * Flexible breadcrumbs component that supports both:
 * 1. Home > Event (for view-event page)
 * 2. Home > Event > Content (for view-content page)
 * 
 * @param {Object} props
 * @param {string} props.eventTitle - Title of the event
 * @param {string} props.eventId - ID of the event for linking
 * @param {string} [props.contentTitle] - Optional content title (if present, shows 3-level breadcrumb)
 */
const EventBreadcrumbs = ({ eventTitle, eventId, contentTitle }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Determine if we're showing content breadcrumb (3 levels) or just event (2 levels)
    const showContentLevel = Boolean(contentTitle);

    return (
        <Box
            sx={{
                mt: { xs: 2, sm: 3 },
                mb: { xs: 1, sm: 2 },
                px: { xs: 1, sm: 2 },
                py: 1.5
            }}
        >
            <Breadcrumbs
                separator={
                    <NavigateNextIcon
                        fontSize="small"
                        sx={{ color: 'primary.main', opacity: 0.8 }}
                    />
                }
                aria-label="navigation breadcrumbs"
                sx={{
                    '& .MuiBreadcrumbs-ol': {
                        flexWrap: isMobile ? 'wrap' : 'nowrap'
                    }
                }}
            >
                {/* Home link - always present */}
                <Link
                    component={RouterLink}
                    to="/"
                    underline="hover"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'primary.main',
                        '&:hover': {
                            color: 'primary.dark',
                            textDecoration: 'underline'
                        },
                        transition: 'color 0.2s'
                    }}
                >
                    <HomeIcon sx={{ mr: 0.5, fontSize: isMobile ? '1.1rem' : '1.3rem' }} />
                    {!isMobile && (
                        <Typography
                            variant="body1"
                            fontWeight="medium"
                            sx={{ fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' } }}
                        >
                            Home
                        </Typography>
                    )}
                </Link>

                {/* Event link or text depending on whether we have content level */}
                {showContentLevel ? (
                    // If showing content level, event becomes a link
                    <Link
                        component={RouterLink}
                        to={`/event/${eventId}`}
                        underline="hover"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'primary.main',
                            '&:hover': {
                                color: 'primary.dark',
                                textDecoration: 'underline'
                            },
                            transition: 'color 0.2s',
                            maxWidth: isMobile ? '150px' : '300px'
                        }}
                    >
                        <Typography
                            variant="body1"
                            fontWeight="medium"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
                            }}
                        >
                            {eventTitle || 'Event'}
                        </Typography>
                    </Link>
                ) : (
                    // If no content level, event is just text (current page)
                    <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{
                            maxWidth: { xs: '250px', sm: '500px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
                        }}
                    >
                        {eventTitle || 'Event'}
                    </Typography>
                )}

                {/* Content level - only shown if contentTitle is provided */}
                {showContentLevel && (
                    <Typography
                        variant="body1"
                        fontWeight="medium"
                        sx={{
                            maxWidth: { xs: '150px', sm: '300px' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                            fontSize: { xs: '0.95rem', sm: '1rem', md: '1.1rem' }
                        }}
                    >
                        {contentTitle}
                    </Typography>
                )}
            </Breadcrumbs>
        </Box>
    );
};

export default EventBreadcrumbs;